import React from "react";
import {
  Card,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const InfoLabel = ({ label, tooltip }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <span>{label}</span>
    <Tooltip title={tooltip} placement="top" arrow>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
    </Tooltip>
  </Stack>
);

const NETWORK_FIELDS = [
  {
    key: "dlaServerIp",
    label: "IP adresa DLA servera",
    tooltip:
      "IP adresa počítača, na ktorom beží Datavision Local Agent. Na túto adresu bude QR čítačka posielať HTTP requesty so skenovanými kódmi.",
  },
  {
    key: "mask",
    label: "Maska podsiete",
    tooltip: "Maska podsiete pre sieťové rozhranie QR čítačky (napr. 255.255.255.0).",
  },
  {
    key: "gateway",
    label: "Predvolená brána",
    tooltip: "IP adresa smerovača (routera) v sieti QR čítačky.",
  },
  {
    key: "dns",
    label: "DNS server",
    tooltip: "IP adresa DNS servera pre QR čítačku (napr. 8.8.8.8).",
  },
];

const AccessSystemTurnstilesSettingsTab = ({
  values,
  errors,
  setFieldValue,
  handleBlur,
  t,
}) => {
  const ts = values?.turnstilesSettings ?? {};
  const tsErrors = errors?.turnstilesSettings ?? {};

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="h6" fontWeight={700}>
            {t("pages.accessSystemSettings.turnstiles.title", {
              defaultValue: "Nastavenie turniketov",
            })}
          </Typography>
          <Divider />
          <Typography variant="body2" color="text.secondary">
            {t("pages.accessSystemSettings.turnstiles.description", {
              defaultValue:
                "Tieto nastavenia sa použijú pre konfiguráciu QR čítačiek a komunikáciu s DLA serverom.",
            })}
          </Typography>
        </Stack>

        {/* Sieťové nastavenia */}
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("pages.accessSystemSettings.turnstiles.networkSection", {
              defaultValue: "Sieťové nastavenia",
            })}
          </Typography>
          <Grid container spacing={2}>
            {NETWORK_FIELDS.map(({ key, label, tooltip }) => {
              const fieldName = `turnstilesSettings.${key}`;
              const fieldError = tsErrors[key];
              return (
                <Grid item xs={12} md={6} key={key}>
                  <TextField
                    fullWidth
                    name={fieldName}
                    value={ts[key] ?? ""}
                    onChange={(e) => setFieldValue(fieldName, e.target.value)}
                    onBlur={handleBlur}
                    label={<InfoLabel label={label} tooltip={tooltip} />}
                    helperText={fieldError || " "}
                    error={Boolean(fieldError)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Stack>

        <Divider />

        {/* Nastavenia QR čítačky */}
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("pages.accessSystemSettings.turnstiles.scannerSection", {
              defaultValue: "Nastavenia QR čítačky (M350)",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("pages.accessSystemSettings.turnstiles.scannerDescription", {
              defaultValue:
                "Parametre zakódované do konfiguračného QR kódu pre čítačku Vguang M350. Po zmene je potrebné vygenerovať nový QR kód a naskenovaním ho nanovo nakonfigurovať.",
            })}
          </Typography>

          <Grid container spacing={2} alignItems="flex-start">
            {/* s_mode — Continuous mode */}
            <Grid item xs={12} md={6}>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ts.scannerSMode === true}
                      onChange={(e) =>
                        setFieldValue(
                          "turnstilesSettings.scannerSMode",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <InfoLabel
                      label={t(
                        "pages.accessSystemSettings.turnstiles.fields.scannerSMode",
                        { defaultValue: "Continuous mode" },
                      )}
                      tooltip="Keď je zapnutý, čítačka je po každom skene okamžite pripravená skenovať ďalší kód (vrátane rovnakého) bez čakania na potvrdenie. Odporúčané pre turnikety s multi-use lístkami."
                    />
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                  {ts.scannerSMode
                    ? t(
                        "pages.accessSystemSettings.turnstiles.fields.scannerSModeOn",
                        { defaultValue: "Zapnuté — s_mode=0 (continuous)" },
                      )
                    : t(
                        "pages.accessSystemSettings.turnstiles.fields.scannerSModeOff",
                        { defaultValue: "Vypnuté — s_mode=1 (single scan)" },
                      )}
                </Typography>
              </Stack>
            </Grid>

            {/* door_report */}
            <Grid item xs={12} md={6}>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ts.scannerDoorReport === true}
                      onChange={(e) =>
                        setFieldValue(
                          "turnstilesSettings.scannerDoorReport",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <InfoLabel
                      label={t(
                        "pages.accessSystemSettings.turnstiles.fields.scannerDoorReport",
                        { defaultValue: "Door report (senzor dverí)" },
                      )}
                      tooltip="Keď je zapnutý, čítačka po každom skene čaká na fyzický signál otvorenia dverí cez hardvérový vstupný pin. Vyžaduje zapojenie senzora do vstupného konektora čítačky. Pre väčšinu inštalácií nechajte vypnuté."
                    />
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                  {ts.scannerDoorReport
                    ? t(
                        "pages.accessSystemSettings.turnstiles.fields.scannerDoorReportOn",
                        { defaultValue: "Zapnuté — door_report=1 (čaká na senzor)" },
                      )
                    : t(
                        "pages.accessSystemSettings.turnstiles.fields.scannerDoorReportOff",
                        { defaultValue: "Vypnuté — door_report=0 (bez senzora)" },
                      )}
                </Typography>
              </Stack>
            </Grid>

            {/* interval */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                name="turnstilesSettings.scannerInterval"
                value={ts.scannerInterval ?? 5000}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setFieldValue(
                    "turnstilesSettings.scannerInterval",
                    Number.isFinite(v) && v >= 0 ? v : 0,
                  );
                }}
                onBlur={handleBlur}
                inputProps={{ min: 0, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">s</InputAdornment>,
                }}
                label={
                  <InfoLabel
                    label={t(
                      "pages.accessSystemSettings.turnstiles.fields.scannerInterval",
                      { defaultValue: "Interval de-duplikácie" },
                    )}
                    tooltip="Čas v sekundách, počas ktorého čítačka ignoruje rovnaký kód po úspešnom skene. Ochrana pred neúmyselným dvojitým načítaním. Hodnota 0 vypína de-duplikáciu úplne. Do QR kódu sa ukladá automaticky v milisekundách."
                  />
                }
                helperText={
                  tsErrors.scannerInterval ||
                  t(
                    "pages.accessSystemSettings.turnstiles.fields.scannerIntervalHelp",
                    { defaultValue: "0 = de-duplikácia vypnutá" },
                  )
                }
                error={Boolean(tsErrors.scannerInterval)}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Card>
  );
};

export default AccessSystemTurnstilesSettingsTab;
